import React, { useState, useEffect, useRef } from 'react';
import { addUser, removeUser, searchUsers, getUsersByIds } from '../services/featureFlagsApi';

const UserManager = ({ flagId, allowedTenants, tenantUserMap, onUpdate }) => {
  const [selectedTenant, setSelectedTenant] = useState(allowedTenants.length > 0 ? allowedTenants[0] : '');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userDetailsMap, setUserDetailsMap] = useState({}); // Map of userId -> user details
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Validate flagId is available
  if (!flagId) {
    return (
      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Users</h3>
        <div className="p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm">
          Error: Feature flag ID is missing. Please refresh the page.
        </div>
      </div>
    );
  }

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidGuid = (guid) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  };

  // Fetch user details for all users in tenantUserMap
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!tenantUserMap || Object.keys(tenantUserMap).length === 0) {
        setUserDetailsMap({});
        return;
      }

      setLoadingUserDetails(true);
      const detailsMap = { ...userDetailsMap };

      try {
        // Fetch details for each tenant
        const fetchPromises = Object.entries(tenantUserMap).map(async ([tenant, userIds]) => {
          if (!userIds || userIds.length === 0) {
            return;
          }

          try {
            // Convert string GUIDs to proper GUID format if needed
            const validUserIds = userIds
              .filter(id => id && typeof id === 'string')
              .map(id => {
                // Remove any extra characters and ensure proper GUID format
                const cleanId = id.trim();
                return cleanId;
              })
              .filter(id => {
                // Validate GUID format
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                return guidRegex.test(id);
              });

            if (validUserIds.length === 0) {
              return;
            }

            // Fetch user details from backend
            const userDetails = await getUsersByIds(tenant, validUserIds);

            // Store details in map (normalize userId to string for consistent lookup)
            userDetails.forEach(user => {
              const userIdKey = String(user.id).toLowerCase();
              detailsMap[userIdKey] = {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName
              };
            });
          } catch (err) {
            console.error(`[UserManager] Error fetching user details for tenant ${tenant}:`, err);
            // Continue with other tenants even if one fails
          }
        });

        await Promise.all(fetchPromises);
        setUserDetailsMap(detailsMap);
      } catch (err) {
        console.error('[UserManager] Error fetching user details:', err);
      } finally {
        setLoadingUserDetails(false);
      }
    };

    fetchUserDetails();
  }, [tenantUserMap]);

  // Debounced search for user suggestions
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!selectedTenant || !newUserEmail || newUserEmail.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchUsers(selectedTenant, newUserEmail);
        setSuggestions(results || []);
        setShowSuggestions(results && results.length > 0);
        setSelectedSuggestionIndex(-1);
      } catch (err) {
        console.error('[UserManager] Error searching users:', err);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [newUserEmail, selectedTenant]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleAddUser(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        } else {
          handleAddUser(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelectSuggestion = (user) => {
    // Store the userId and display it in the input field
    setSelectedUserId(user.id);
    setNewUserEmail(user.id); // Display the userId (GUID) in the input
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef.current?.focus();
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!flagId) {
      setError('Feature flag ID is missing');
      return;
    }
    if (!selectedTenant) {
      setError('Please select a tenant');
      return;
    }
    if (!newUserEmail.trim()) {
      setError('User ID or email address is required');
      return;
    }

    const inputValue = newUserEmail.trim();
    const isGuid = isValidGuid(inputValue);
    const isEmail = isValidEmail(inputValue);

    if (!isGuid && !isEmail) {
      setError('Please enter a valid email address or select a user from the suggestions');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setShowSuggestions(false);

      // If we have a selected userId from clicking a suggestion, use it
      // Otherwise, if input is a GUID, use it directly
      // Otherwise, use the email for lookup
      const userIdToUse = selectedUserId || (isGuid ? inputValue : null);
      const emailToUse = isEmail ? inputValue : null;

      console.log('[UserManager] Adding user:', {
        flagId,
        tenant: selectedTenant,
        userId: userIdToUse,
        email: emailToUse
      });

      await addUser(flagId, selectedTenant, userIdToUse || emailToUse);
      setNewUserEmail('');
      setSelectedUserId(null);
      setSuggestions([]);
      onUpdate();
    } catch (err) {
      setError(err.details?.error || err.message || 'Failed to add user');
      console.error('[UserManager] Error adding user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUser = async (tenant, userId) => {
    if (!window.confirm(`Are you sure you want to remove user "${userId}" from tenant "${tenant}"?`)) {
      return;
    }

    if (!flagId) {
      setError('Feature flag ID is missing');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      console.log('[UserManager] Removing user:', { flagId, tenant, userId });
      await removeUser(flagId, tenant, userId);
      onUpdate();
    } catch (err) {
      setError(err.details?.error || err.message || 'Failed to remove user');
      console.error('[UserManager] Error removing user:', err);
    } finally {
      setSaving(false);
    }
  };

  if (allowedTenants.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Users</h3>
        <p className="text-gray-500 dark:text-slate-400 italic">Add tenants first to manage users</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
      <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-slate-700">Users by Tenant</h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm">{error}</div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
        <select
          value={selectedTenant}
          onChange={(e) => {
            setSelectedTenant(e.target.value);
            setNewUserEmail('');
            setSelectedUserId(null);
            setSuggestions([]);
            setShowSuggestions(false);
          }}
          disabled={saving}
          className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 min-w-[150px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          {allowedTenants.map(tenant => (
            <option key={tenant} value={tenant}>
              {tenant}
            </option>
          ))}
        </select>
        <form onSubmit={handleAddUser} className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={newUserEmail}
              onChange={(e) => {
                setNewUserEmail(e.target.value);
                setSelectedUserId(null);
                setShowSuggestions(true);
              }}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder="Enter user email or select from suggestions"
              disabled={saving}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            {loadingSuggestions && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-gray-200 dark:border-slate-600 border-t-blue-600 rounded-full animate-spin" />
              </div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[1000]"
              >
                {suggestions.map((user, index) => (
                  <div
                    key={user.id}
                    onClick={() => handleSelectSuggestion(user)}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-slate-800 last:border-0 ${
                      index === selectedSuggestionIndex ? 'bg-blue-50 dark:bg-blue-950' : 'hover:bg-gray-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{user.email}</div>
                    {(user.firstName || user.lastName) && (
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={saving || !newUserEmail.trim()}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 shrink-0"
          >
            Add User
          </button>
        </form>
      </div>

      <div className="space-y-4">
        {allowedTenants.map((tenant) => {
          const users = tenantUserMap[tenant] || [];
          return (
            <div key={tenant} className="bg-white dark:bg-slate-900 p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 pb-2 border-b border-gray-200 dark:border-slate-700">
                {tenant} ({users.length} users)
              </h4>
              {users.length === 0 ? (
                <p className="text-gray-500 dark:text-slate-400 italic text-sm">No users for this tenant</p>
              ) : (
                <ul className="space-y-2">
                  {users.map((userId) => {
                    const userIdKey = String(userId).toLowerCase();
                    const userDetails = userDetailsMap[userIdKey];
                    return (
                      <li
                        key={userId}
                        className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 p-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-mono text-sm text-gray-800 dark:text-gray-200 break-all">{userId}</span>
                          {userDetails && (
                            <div className="mt-2 p-2 bg-white dark:bg-slate-900 rounded border-l-4 border-blue-500 space-y-1">
                              {userDetails.email && (
                                <div className="flex gap-2 text-xs">
                                  <span className="font-semibold text-gray-500 dark:text-slate-400">Email:</span>
                                  <span className="text-gray-800 dark:text-gray-200">{userDetails.email}</span>
                                </div>
                              )}
                              {(userDetails.firstName || userDetails.lastName) && (
                                <div className="flex gap-2 text-xs">
                                  <span className="font-semibold text-gray-500 dark:text-slate-400">Name:</span>
                                  <span className="text-gray-800 dark:text-gray-200">
                                    {[userDetails.firstName, userDetails.lastName].filter(Boolean).join(' ') || 'N/A'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {loadingUserDetails && !userDetails && (
                            <div className="mt-2 text-xs text-gray-400 dark:text-slate-500 italic">Loading details...</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveUser(tenant, userId)}
                          disabled={saving}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-60 shrink-0 self-start"
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserManager;
